// İSG uzmanı portalı — Risk Değerlendirme yaşam döngüsü aksiyonları (api-contract §19.2).
// Butonlar **duruma göre koşullu** gösterilir: Draft/Revised → Gönder (submit); Submitted →
// Onayla (approve); Active → Gözden Geçir (review); UnderReview → Revize Et (revise); herhangi
// (Archived hariç) → Arşivle (archive). Her aksiyon sonrası değerlendirme detayı + liste
// invalidate edilir; durum backend'in döndürdüğü değerdir (UI kendi hesaplamaz). Yanlış durumdan
// geçiş BE'de `409 RISK_ASSESSMENT_INVALID_STATE` verir — butonlar zaten status'a göre gizli
// olduğundan UI'da tetiklenmez.

import { Button, ConfirmDialog, type RiskAssessmentDto, riskApi } from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCircle2, PencilRuler, RotateCcw, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { riskKeys } from '../../lib/riskKeys';

interface RiskLifecycleActionsProps {
  assessment: RiskAssessmentDto;
}

type ConfirmKind = 'submit' | 'approve' | 'review' | 'revise' | 'archive' | null;

export function RiskLifecycleActions({ assessment }: RiskLifecycleActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<ConfirmKind>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: riskKeys.detail(assessment.uuid) });
    queryClient.invalidateQueries({ queryKey: riskKeys.list() });
  }

  const submitMutation = useMutation({
    mutationFn: () => riskApi.submitRiskAssessment(assessment.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const approveMutation = useMutation({
    mutationFn: () => riskApi.approveRiskAssessment(assessment.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const reviewMutation = useMutation({
    mutationFn: () => riskApi.reviewRiskAssessment(assessment.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const reviseMutation = useMutation({
    mutationFn: () => riskApi.reviseRiskAssessment(assessment.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const archiveMutation = useMutation({
    mutationFn: () => riskApi.archiveRiskAssessment(assessment.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });

  const status = assessment.status;
  const canSubmit = status === 'Draft' || status === 'Revised';
  const canApprove = status === 'Submitted';
  const canReview = status === 'Active';
  const canRevise = status === 'UnderReview';
  const canArchive = status !== 'Archived';

  const activeConfirm = useMemo(() => {
    switch (confirm) {
      case 'submit':
        return { mutation: submitMutation, variant: 'primary' as const };
      case 'approve':
        return { mutation: approveMutation, variant: 'primary' as const };
      case 'review':
        return { mutation: reviewMutation, variant: 'primary' as const };
      case 'revise':
        return { mutation: reviseMutation, variant: 'primary' as const };
      case 'archive':
        return { mutation: archiveMutation, variant: 'danger' as const };
      default:
        return null;
    }
  }, [confirm, submitMutation, approveMutation, reviewMutation, reviseMutation, archiveMutation]);

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {canSubmit ? (
        <Button variant="primary" size="sm" onClick={() => setConfirm('submit')}>
          <Send className="h-4 w-4" aria-hidden="true" />
          {t('risk.lifecycle.submit')}
        </Button>
      ) : null}
      {canApprove ? (
        <Button variant="primary" size="sm" onClick={() => setConfirm('approve')}>
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {t('risk.lifecycle.approve')}
        </Button>
      ) : null}
      {canReview ? (
        <Button variant="secondary" size="sm" onClick={() => setConfirm('review')}>
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          {t('risk.lifecycle.review')}
        </Button>
      ) : null}
      {canRevise ? (
        <Button variant="primary" size="sm" onClick={() => setConfirm('revise')}>
          <PencilRuler className="h-4 w-4" aria-hidden="true" />
          {t('risk.lifecycle.revise')}
        </Button>
      ) : null}
      {canArchive ? (
        <Button variant="secondary" size="sm" onClick={() => setConfirm('archive')}>
          <Archive className="h-4 w-4" aria-hidden="true" />
          {t('risk.lifecycle.archive')}
        </Button>
      ) : null}

      {activeConfirm && confirm ? (
        <ConfirmDialog
          open={confirm !== null}
          onOpenChange={(open) => !open && setConfirm(null)}
          title={t(`risk.lifecycle.${confirm}.title`)}
          body={t(`risk.lifecycle.${confirm}.body`)}
          confirmLabel={t(`risk.lifecycle.${confirm}.confirm`)}
          confirmVariant={activeConfirm.variant}
          loading={activeConfirm.mutation.isPending}
          error={activeConfirm.mutation.isError ? activeConfirm.mutation.error : undefined}
          onConfirm={() => activeConfirm.mutation.mutate()}
        />
      ) : null}
    </div>
  );
}
